import { useCallback, useEffect, useRef, useState } from "react";

import { captureException } from "@sentry/react";

export interface UseAudioGraphOptions {
  /**
   * When false (e.g. photo mode, or callers that never record), the raw stream
   * is passed through unchanged and no AudioContext is created.
   */
  enabled: boolean;
}

export interface UseAudioGraphReturn {
  /**
   * Stream to hand to the recorder.
   *
   * When enabled and the raw stream carries audio, the audio is routed through
   * a constant Web Audio destination node, so the underlying microphone can be
   * swapped mid-recording (by replacing the audio track on the raw stream)
   * without interrupting the recording. Otherwise this is the raw stream
   * unchanged.
   */
  recordingStream: MediaStream | null;
  /**
   * Resume the AudioContext. Call on a user gesture / at record start so a
   * context that began suspended does not record silence.
   */
  resume: () => void;
}

/**
 * Declarative Web Audio transform between the raw capture stream and the
 * recorder. The recorded audio track (the destination node's track) stays
 * constant while the microphone feeding it can be re-wired underneath, which is
 * what makes gapless mid-recording microphone fallback possible — `MediaRecorder`
 * records the track instances present at `start()` and never sees a swap on the
 * raw stream.
 */
export function useAudioGraph(
  rawStream: MediaStream | null,
  { enabled }: UseAudioGraphOptions
): UseAudioGraphReturn {
  const [recordingStream, setRecordingStream] = useState<MediaStream | null>(
    null
  );

  const contextRef = useRef<AudioContext | null>(null);
  const destinationRef = useRef<MediaStreamAudioDestinationNode | null>(null);
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const wiredAudioTrackIdRef = useRef<string | null>(null);

  const detachSource = useCallback(() => {
    sourceRef.current?.disconnect();
    sourceRef.current = null;
    wiredAudioTrackIdRef.current = null;
  }, []);

  const cleanup = useCallback(() => {
    detachSource();
    destinationRef.current?.disconnect();
    destinationRef.current = null;
    contextRef.current
      ?.close()
      .catch((error) => captureException(error, { level: "warning" }));
    contextRef.current = null;
  }, [detachSource]);

  useEffect(() => {
    const audioTrack = rawStream?.getAudioTracks()[0] ?? null;

    // Disabled (e.g. photo mode): close the context so nothing keeps running
    // while audio routing is not needed. It is recreated lazily on re-enable.
    if (!enabled) {
      cleanup();
      setRecordingStream(rawStream);
      return;
    }

    // Transient pass-through while enabled (stream restart in flight, or no
    // audio to route): keep the context warm to avoid churn on every swap.
    if (rawStream == null || audioTrack == null) {
      detachSource();
      setRecordingStream(rawStream);
      return;
    }

    if (window.AudioContext == null) {
      // No Web Audio support: record the raw stream directly (no gapless swap).
      setRecordingStream(rawStream);
      return;
    }

    if (contextRef.current == null) {
      contextRef.current = new window.AudioContext();
      destinationRef.current =
        contextRef.current.createMediaStreamDestination();
    }
    const context = contextRef.current;
    const destination = destinationRef.current;
    if (destination == null) return;

    // Re-wire the source whenever the microphone track changes (recovery), so
    // the constant destination track keeps producing audio across the swap.
    if (wiredAudioTrackIdRef.current !== audioTrack.id) {
      sourceRef.current?.disconnect();
      sourceRef.current = context.createMediaStreamSource(
        new MediaStream([audioTrack])
      );
      sourceRef.current.connect(destination);
      wiredAudioTrackIdRef.current = audioTrack.id;
    }

    // Rebuild the recording stream from the current video tracks plus the
    // constant destination audio track in case the raw stream was swapped out.
    const destinationTrack = destination.stream.getAudioTracks()[0];
    if (destinationTrack != null) {
      setRecordingStream(
        new MediaStream([...rawStream.getVideoTracks(), destinationTrack])
      );
    }
  }, [rawStream, enabled, detachSource, cleanup]);

  useEffect(() => {
    return () => cleanup();
  }, [cleanup]);

  const resume = useCallback(() => {
    if (contextRef.current?.state === "suspended") {
      contextRef.current
        .resume()
        .catch((error) => captureException(error, { level: "warning" }));
    }
  }, []);

  return { recordingStream, resume };
}
